import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Printer, Check, X, Loader2, Plus, Trash2, Settings, ArrowLeft } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';

const BACKEND_URL = window.location.origin;

const PrinterSettingsPage = () => {
  const navigate = useNavigate();
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [configuredPrinters, setConfiguredPrinters] = useState([]);
  const [printerStatus, setPrinterStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingPrinter, setTestingPrinter] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [printerType, setPrinterType] = useState('kot');
  const [description, setDescription] = useState('');
  const [apiKey, setApiKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [savedKey, setSavedKey] = useState("");
  const [editingKey, setEditingKey] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // First check API key status
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${BACKEND_URL}/api/printers/api-key`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.configured) {
          setSavedKey(response.data.masked_key || "Configured");
        }
      } catch (error) {
        console.error("Failed to check API key:", error);
      } finally {
        // Then fetch all printer data (only once)
        await fetchData();
      }
    };

    initializeData();

    // Refresh status every 30 seconds
    const interval = setInterval(() => {
      fetchPrinterStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, []);
  const checkApiKey = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${BACKEND_URL}/api/printers/api-key`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.configured) {
        setSavedKey(response.data.masked_key || "Configured");
      }

      // Refresh printer data after API key check
      await fetchData();
    } catch (error) {
      console.error(error);
      // Still refresh printer data even if API key check fails
      await fetchData();
    }
  };

const saveApiKey = async () => {
  try {
    setSavingKey(true);

    const token = localStorage.getItem("token");

    await axios.post(
      `${BACKEND_URL}/api/printers/api-key`,
      { api_key: apiKey },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    setEditingKey(false);
    setApiKey("");
    checkApiKey();

  } catch (error) {
    alert("Failed to save API key");
  } finally {
    setSavingKey(false);
  }
};
  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchAvailablePrinters(),
        fetchConfiguredPrinters(),
        fetchPrinterStatus()
      ]);
    } catch (error) {
      console.error('Failed to fetch printer data:', error);
    } finally {
      setLoading(false);
    }
  };

const fetchAvailablePrinters = async () => {
  try {
    const token = localStorage.getItem("token");

    const response = await axios.get(
      `${BACKEND_URL}/api/printers/available`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    setAvailablePrinters(response.data.printers || []);

  } catch (error) {
    console.error(error);
  }
};
  const fetchConfiguredPrinters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/printers/configured`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfiguredPrinters(response.data.printers || []);
    } catch (error) {
      console.error('Failed to fetch configured printers:', error);
    }
  };

  const fetchPrinterStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/printers/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrinterStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch printer status:', error);
    }
  };

  const handleAddPrinter = async () => {
    if (!selectedPrinter) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/printers/configure`,
        {
          printer_id: selectedPrinter.id,
          printer_name: selectedPrinter.name,
          printer_type: printerType,
          description: description || selectedPrinter.description || '',
          is_active: true
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setShowAddModal(false);
      setSelectedPrinter(null);
      setPrinterType('kot');
      setDescription('');
      fetchData();
      alert('Printer configured successfully!');
    } catch (error) {
      console.error('Failed to configure printer:', error);
      alert(error.response?.data?.detail || 'Failed to configure printer');
    }
  };

  const handleDeletePrinter = async (printerId) => {
    if (!window.confirm('Are you sure you want to remove this printer configuration?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/printers/configure/${printerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert('Printer configuration removed');
    } catch (error) {
      console.error('Failed to delete printer:', error);
      alert(error.response?.data?.detail || 'Failed to delete printer');
    }
  };

  const handleTestPrinter = async (printerId) => {
    setTestingPrinter(printerId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/printers/test/${printerId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        alert('✅ ' + response.data.message);
      } else {
        alert('❌ ' + response.data.message);
      }
    } catch (error) {
      console.error('Printer test failed:', error);
      alert('❌ Printer test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestingPrinter(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <AdminLayout>
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Go Back"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <Settings className="w-8 h-8" />
              Printer Settings
            </h1>
            <p className="text-gray-600 mt-1">Configure and manage your thermal printers</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={!savedKey}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Printer
        </button>
      </div>
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">PrintNode API Key</h2>
          <p className="text-sm text-gray-500">
            Manage your PrintNode connection key
          </p>
        </div>

        {!editingKey && (
          <button
            onClick={() => setEditingKey(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {savedKey ? "Edit Key" : "Add Key"}
          </button>
        )}
      </div>

      {!editingKey ? (
        <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
          {savedKey || "No API Key Configured"}
        </div>
      ) : (
        <div className="space-y-3">
          <input
            type="text"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Enter PrintNode API Key"
            className="w-full border rounded-lg p-3"
          />

          <div className="flex gap-3">
            <button
              onClick={saveApiKey}
              disabled={!apiKey || savingKey}
              className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              {savingKey ? "Saving..." : "Save"}
            </button>

            <button
              onClick={() => {
                setEditingKey(false);
                setApiKey("");
              }}
              className="px-5 py-2 border rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>

    {savedKey && (
  <>
      {/* Printer Status Card */}
      {printerStatus && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Printer className="w-6 h-6" />
            Printer Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="font-medium">KOT Printer</span>
              <span className={`flex items-center gap-2 ${printerStatus.kot_printer_online ? 'text-green-600' : 'text-red-600'}`}>
                {printerStatus.kot_printer_online ? (
                  <><Check className="w-5 h-5" /> Online</>
                ) : (
                  <><X className="w-5 h-5" /> Offline</>
                )}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="font-medium">Receipt Printer</span>
              <span className={`flex items-center gap-2 ${printerStatus.receipt_printer_online ? 'text-green-600' : 'text-red-600'}`}>
                {printerStatus.receipt_printer_online ? (
                  <><Check className="w-5 h-5" /> Online</>
                ) : (
                  <><X className="w-5 h-5" /> Offline</>
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Configured Printers */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Configured Printers</h2>
        {configuredPrinters.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No printers configured yet. Add a printer to get started.</p>
        ) : (
          <div className="space-y-4">
            {configuredPrinters.map((printer) => {
              const isOnline = printerStatus?.printers?.find(p => p.printer_id === printer.printer_id)?.status === 'online';
              return (
                <div key={printer.printer_id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                      <div>
                        <h3 className="font-semibold text-lg">{printer.printer_name}</h3>
                        <p className="text-sm text-gray-600">
                          Type: <span className="font-medium uppercase">{printer.printer_type}</span>
                          {printer.description && ` • ${printer.description}`}
                        </p>
                        <p className="text-xs text-gray-500">Printer ID: {printer.printer_id}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleTestPrinter(printer.printer_id)}
                        disabled={testingPrinter === printer.printer_id}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
                      >
                        {testingPrinter === printer.printer_id ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Testing...</>
                        ) : (
                          <>Test</>
                        )}
                      </button>
                      <button
                        onClick={() => handleDeletePrinter(printer.printer_id)}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Available Printers (PrintNode)</h2>
        {availablePrinters.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-2">No printers detected from PrintNode</p>
            <p className="text-sm text-gray-400">Make sure PrintNode Client is running and printers are connected</p>
          </div>
        ) : (
          <div className="space-y-3">
            {availablePrinters.map((printer) => (
              <div key={printer.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{printer.name}</h3>
                    <p className="text-sm text-gray-600">
                      {printer.description || 'No description'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Computer: {printer.computer?.name || 'Unknown'} • ID: {printer.id}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    printer.state === 'online' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {printer.state || 'Unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
  </>
)}
      {/* Add Printer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Add Printer Configuration</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Printer</label>
                <select
                  value={selectedPrinter?.id || ''}
                  onChange={(e) => {
                    const printer = availablePrinters.find(p => p.id === parseInt(e.target.value));
                    setSelectedPrinter(printer);
                  }}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="">-- Select a printer --</option>
                  {availablePrinters.map((printer) => (
                    <option key={printer.id} value={printer.id}>
                      {printer.name} (ID: {printer.id})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Printer Type</label>
                <select
                  value={printerType}
                  onChange={(e) => setPrinterType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="kot">KOT Printer (Kitchen)</option>
                  <option value="receipt">Receipt Printer (Customer)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Main kitchen printer"
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedPrinter(null);
                  setPrinterType('kot');
                  setDescription('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPrinter}
                disabled={!selectedPrinter}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Printer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default PrinterSettingsPage;
